import type { ElementType, ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface ContainerProps {
  as?: ElementType;
  /** `prose` narrows to a comfortable reading measure for long-form text. */
  width?: 'page' | 'prose';
  className?: string;
  children: ReactNode;
}

/** Max width and responsive gutters. The only place page width is decided. */
export function Container({
  as: Component = 'div',
  width = 'page',
  className,
  children,
}: ContainerProps) {
  return (
    <Component
      className={cn(
        'mx-auto w-full px-4 sm:px-6 lg:px-8',
        width === 'page' ? 'max-w-page' : 'max-w-prose',
        className,
      )}
    >
      {children}
    </Component>
  );
}

export interface SectionProps {
  as?: ElementType;
  className?: string;
  spacing?: 'none' | 'sm' | 'md' | 'lg';
  /**
   * Scopes the dark semantic tier to this section only and gives it the solid
   * Ink ground — the closing CTA band on the home page, which runs the dark
   * register while the rest of the page is Paper. See `PublicLayout`'s doc
   * comment for how `data-theme` scoping works.
   */
  theme?: 'dark';
  children?: ReactNode;
}

const spacings = {
  none: '',
  sm: 'py-8 sm:py-10',
  md: 'py-12 sm:py-16',
  lg: 'py-16 sm:py-24',
} as const;

/**
 * A page section with consistent vertical rhythm.
 *
 * Renders `null` when it has no children. That is the mechanical enforcement of
 * "absence hides" (ADR-009): a section with no content cannot leave a heading,
 * a border or a block of padding behind, because it does not render at all.
 */
export function Section({
  as: Component = 'section',
  className,
  spacing = 'md',
  theme,
  children,
}: SectionProps) {
  if (children === null || children === undefined || children === false) return null;

  return (
    <Component
      data-theme={theme}
      className={cn(spacings[spacing], theme === 'dark' && 'hs-band', className)}
    >
      {children}
    </Component>
  );
}

export interface GridProps {
  /** Columns at the largest breakpoint. Below that the grid steps down. */
  columns?: 2 | 3 | 4;
  className?: string;
  children: ReactNode;
}

const columnPresets = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
} as const;

export function Grid({ columns = 3, className, children }: GridProps) {
  return <div className={cn('grid gap-6', columnPresets[columns], className)}>{children}</div>;
}
